import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface Toast {
	id: string;
	type: 'success' | 'error' | 'info' | 'warning';
	title?: string;
	message: string;
	duration?: number;
	dismissible?: boolean;
}

@Injectable({
	providedIn: 'root',
})
export class ToastService {
	private readonly toastsSubject = new BehaviorSubject<Toast[]>([]);
	public toasts$: Observable<Toast[]> = this.toastsSubject.asObservable();

	// Callback for animated dismissal
	public onAnimatedDismiss?: (id: string) => void;

	private generateId(): string {
		return Math.random().toString(36).substr(2, 9);
	}

	/**
	 * Show a toast notification
	 */
	show(toast: Omit<Toast, 'id'>): void {
		const newToast: Toast = {
			id: this.generateId(),
			dismissible: true,
			...toast,
			// Ensure we always have a valid duration, override if undefined/null
			duration: toast.duration ?? 3000,
		};

		const currentToasts = this.toastsSubject.value;
		this.toastsSubject.next([...currentToasts, newToast]);

		// Auto-dismiss after duration
		if (newToast.duration && newToast.duration > 0) {
			setTimeout(() => {
				// Use animated dismiss if available, otherwise direct dismiss
				if (this.onAnimatedDismiss) {
					this.onAnimatedDismiss(newToast.id);
				} else {
					this.dismiss(newToast.id);
				}
			}, newToast.duration);
		}
	}

	/**
	 * Show success toast
	 */
	success(message: string, title?: string, duration?: number): void {
		this.show({
			type: 'success',
			message,
			title,
			duration,
		});
	}

	/**
	 * Show error toast
	 */
	error(message: string, title?: string, duration?: number): void {
		this.show({
			type: 'error',
			message,
			title,
			duration: duration || 5000, // Errors stay longer by default
		});
	}

	/**
	 * Show info toast
	 */
	info(message: string, title?: string, duration?: number): void {
		this.show({
			type: 'info',
			message,
			title,
			duration,
		});
	}

	/**
	 * Show warning toast
	 */
	warning(message: string, title?: string, duration?: number): void {
		this.show({
			type: 'warning',
			message,
			title,
			duration: duration || 4000,
		});
	}

	/**
	 * Dismiss a specific toast
	 */
	dismiss(id: string): void {
		const currentToasts = this.toastsSubject.value;
		const filteredToasts = currentToasts.filter((toast) => toast.id !== id);
		this.toastsSubject.next(filteredToasts);
	}

	/**
	 * Clear all toasts
	 */
	clear(): void {
		this.toastsSubject.next([]);
	}
}
